import React, { useEffect, useState } from "react";
import SalesReportService from "../../../services/OrderingServices/SalesReportService";
import {
  Button,
  Card,
  CardBody,
  Col,
  Row,
  Container,
  InputGroup,
  InputGroupText,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";
import NumberWithCommas from "../../../pages/Pages/Starter/NumberWithCommas";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import { formatDateTimes } from "../../../Components/Common/FormatDate";
import { Link, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import ItemService from "../../../services/OrderingServices/ItemService";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import toast from "react-hot-toast";
import { exportToExcel } from "../../../utilis/exportToExcel";

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const TransactionReport = () => {
  const { id } = useParams();
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [productData, setProductData] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [dateRange, setDateRange] = useState([]);

  // Summary totals (for ALL data, not just current page)
  const [summaryTotals, setSummaryTotals] = useState({
    totalQuantity: 0,
    totalDebit: 0,
    totalCredit: 0,
    totalAmount: 0,
  });

  // Export modal states
  const [exportModal, setExportModal] = useState(false);
  const [exportDateRange, setExportDateRange] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);

  // Format helper
  const formatDate = (date) => date?.toLocaleDateString("en-CA");

  const navigate = useNavigate();
  const breadcrumbItems = [
    { title: "Back To List", onClick: () => navigate(-1) },
  ];

  // Fetch product list
  const fetchItemList = async () => {
    try {
      setLoading(true);
      const response = await ItemService.itemList();
      setProductData(response?.items || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItemList();
  }, []);

  // Fetch summary totals for ALL data (not paginated)
  const fetchSummaryTotals = async (
    from = fromDate,
    to = toDate,
    category = selectedCategory
  ) => {
    if (!from || !to) return;

    try {
      // Fetch all data to calculate totals (you may need a separate API endpoint for this)
      const response = await SalesReportService.getTransactionList({
        productId: category?.value || id,
        fromDate: formatDate(from),
        toDate: formatDate(to),
        page: 1,
        pageSize: 999999, // Get all records for totals
      });

      const allRecords = response?.items || [];

      // Calculate totals from ALL data
      const totals = {
        totalQuantity: allRecords.reduce(
          (sum, item) => sum + (item.quantity || 0),
          0
        ),
        totalDebit: allRecords.reduce(
          (sum, item) => sum + (item.debit || 0),
          0
        ),
        totalCredit: allRecords.reduce(
          (sum, item) => sum + (item.credit || 0),
          0
        ),
        totalAmount: allRecords.reduce(
          (sum, item) => sum + (item.totalAmount || 0),
          0
        ),
      };

      setSummaryTotals(totals);
    } catch (error) {
      console.error("Error fetching summary totals:", error);
      setSummaryTotals({
        totalQuantity: 0,
        totalDebit: 0,
        totalCredit: 0,
        totalAmount: 0,
      });
    }
  };

  // Fetch transaction data (paginated)
  const fetchData = async (
    page = currentPage,
    size = pageSize,
    from = fromDate,
    to = toDate,
    category = selectedCategory
  ) => {
    if (!from || !to) return;

    setLoading(true);
    try {
      const response = await SalesReportService.getTransactionList({
        productId: category?.value || id,
        fromDate: formatDate(from),
        toDate: formatDate(to),
        page,
        pageSize: size,
      });

      setAllData(response?.items || []);
      setTotalRows(response?.totalCount || 0);

      // Fetch summary totals whenever data is fetched
      await fetchSummaryTotals(from, to, category);
    } catch (error) {
      console.error(error);
      setAllData([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  // Table columns
  const columns = [
    {
      name: "S.N",
      selector: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: "75px",
    },
    {
      name: "Product",
      selector: (row) => row.productName,
      cell: (row) => (
        <span title={`${row?.productName}`} className="cursor-pointer">
          {row.productName}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Quantity",
      selector: (row) => row.quantity,
      sortable: true,
      right: true,
    },
    {
      name: "Unit Price",
      selector: (row) => row.unitPrice,
      cell: (row) => <NumberWithCommas number={row.unitPrice} />,
      sortable: true,
      right: true,
    },
    {
      name: "Transaction Date",
      selector: (row) => formatDateTimes(row?.transactionDate),
      sortable: true,
      width: "14vw",
    },
    {
      name: "Debit",
      selector: (row) => row.debit,
      cell: (row) => <NumberWithCommas number={row.debit} />,
      sortable: true,
      right: true,
    },
    {
      name: "Credit",
      selector: (row) => row.credit,
      cell: (row) => <NumberWithCommas number={row.credit} />,
      sortable: true,
      right: true,
    },
    {
      name: "Total",
      selector: (row) => row.totalAmount,
      sortable: true,
      right: true,
      cell: (row) => <NumberWithCommas number={row.totalAmount} />,
    },
  ];

  const categoryOptions =
    productData?.map((item) => ({ value: item.id, label: item.name })) || [];

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchData(page, pageSize);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPageSize(newPerPage);
    setCurrentPage(page);
    fetchData(page, newPerPage);
  };

  const handleGenerate = () => {
    setCurrentPage(1);
    fetchData(1, pageSize);
  };

  const getDateRangeDisplay = () => {
    if (!fromDate || !toDate) return "Select Date Range";
    const start = new Date(fromDate).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    const end = new Date(toDate).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${start} - ${end}`;
  };

  // Handle export with date range
  const handleExportWithDateRange = async () => {
    if (!fromDate || !toDate) {
      toast.error("Please select a valid date range");
      return;
    }

    setExportLoading(true);
    try {
      const startDate = formatLocalDate(fromDate);
      const endDate = formatLocalDate(toDate);

      const response = await SalesReportService.getTransactionExportList({
        productId: selectedCategory?.value || id,
        fromDate: startDate,
        toDate: endDate,
        page: 1,
        pageSize: 10000,
      });

      const exportData = response?.items || [];

      if (exportData.length === 0) {
        toast.error("No data found for the selected filters");
        setExportLoading(false);
        return;
      }

      const columnMapping = {
        "S.N": (row, index) => index + 1,
        Product: "productName",
        Quantity: "quantity",
        "Unit Price": "unitPrice",
        "Transaction Date": (row) => formatDateTimes(row.transactionDate),
        Debit: "debit",
        Credit: "credit",
        "Total Amount": "totalAmount",
      };

      exportToExcel(
        exportData,
        columnMapping,
        `TransactionReport_${startDate}_to_${endDate}.xlsx`
      );

      toast.success("Export successful!");
      setExportLoading(false);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data");
    } finally {
      setExportLoading(false);
    }
  };

  // Default: last 1 month on mount + auto-fetch
  useEffect(() => {
    const today = new Date();
    const lastMonth = new Date();
    lastMonth.setMonth(today.getMonth() - 1);

    setFromDate(lastMonth);
    setToDate(today);
    setDateRange([lastMonth, today]);

    // Fetch data correctly with default page and pageSize
    fetchData(1, pageSize, lastMonth, today, selectedCategory);
  }, []);

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb
          title="Transaction Report"
          pageTitle="Reports"
          breadcrumbItems={breadcrumbItems}
        />

        <div
          style={{
            background: "#ffffff",
            borderRadius: "0.5rem",
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            border: "1px solid #eee",
            overflow: "hidden",
          }}
        >
          {/* Main Content */}
          <div className="p-3 p-lg-4 mt-1">
            {/* Filters */}
            <Row className="mb-3 g-2 align-items-center">
              {/* Title & date display */}
              <Col md={4}>
                <div>
                  <h5 className="mb-1 fw-bold text-dark">Transaction Report</h5>
                  <p className="text-muted small mb-0">
                    {getDateRangeDisplay()}
                  </p>
                </div>
              </Col>

              {/* Filters and buttons */}
              <Col md={8}>
                <Row className="g-2 align-items-end">
                  <Col md={4}>
                    <Select
                      isClearable
                      options={categoryOptions}
                      value={selectedCategory}
                      onChange={setSelectedCategory}
                      placeholder="Filter by product"
                      className="fs-6"
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </Col>

                  <Col md={4}>
                    <InputGroup>
                      <Flatpickr
                        options={{ mode: "range", dateFormat: "Y-m-d" }}
                        value={dateRange}
                        onChange={(dates) => {
                          if (dates.length === 2) {
                            setDateRange(dates);
                            setFromDate(dates[0]);
                            setToDate(dates[1]);
                          }
                        }}
                        placeholder="Select Date Range"
                        className="form-control"
                      />
                      <InputGroupText style={{ cursor: "pointer" }}>
                        <i className="bx bx-calendar"></i>
                      </InputGroupText>
                    </InputGroup>
                  </Col>

                  <Col md={2}>
                    <Button
                      color="primary"
                      className="w-100"
                      onClick={handleGenerate}
                    >
                      <i className="bx bx-search-alt me-1"></i>
                      Generate
                    </Button>
                  </Col>

                  <Col md={2}>
                    <Button
                      color="success"
                      className="w-100"
                      onClick={handleExportWithDateRange}
                      disabled={exportLoading}
                    >
                      <i className="bx bx-export me-1"></i>
                      {exportLoading ? "Exporting..." : "Export"}
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>

            {/* Summary Footer - Now shows totals from ALL data */}
            {allData.length > 0 && (
              <div
                className="p-3 mt-4 mb-3"
                style={{
                  background: "#f8f9fa",
                  borderRadius: "0.375rem",
                  border: "1px solid #dee2e6",
                }}
              >
                <Row className="fw-bold">
                  <Col md={3}>
                    <h6 className="mb-0 text-dark">Total Summary</h6>
                    <small className="text-muted">
                      (All {totalRows} records)
                    </small>
                  </Col>
                  <Col md={9}>
                    <Row>
                      <Col md={3} className="text-end">
                        <span className="text-muted me-2">Qty:</span>
                        <span className="text-dark">
                          {summaryTotals.totalQuantity}
                        </span>
                      </Col>
                      <Col md={3} className="text-end">
                        <span className="text-muted me-2">Debit:</span>
                        <span className="text-dark">
                          <NumberWithCommas number={summaryTotals.totalDebit} />
                        </span>
                      </Col>
                      <Col md={3} className="text-end">
                        <span className="text-muted me-2">Credit:</span>
                        <span className="text-dark">
                          <NumberWithCommas
                            number={summaryTotals.totalCredit}
                          />
                        </span>
                      </Col>
                      <Col md={3} className="text-end">
                        <span className="text-muted me-2">Total:</span>
                        <span className="text-dark">
                          <NumberWithCommas
                            number={summaryTotals.totalAmount}
                          />
                        </span>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </div>
            )}
            {/* Table */}
            <CustomDataTable
              responsive
              striped
              pagination
              paginationTotalRows={totalRows}
              paginationDefaultPage={currentPage}
              paginationPerPage={pageSize}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
              fixedHeader
              highlightOnHover
              pointerOnHover
              persistTableHead
              progressPending={loading}
              paginationServer
              columns={columns}
              data={allData}
              customStyles={{
                headCells: {
                  style: {
                    fontSize: "0.92rem",
                    fontWeight: 610,
                  },
                },
                rows: {
                  style: {
                    fontSize: "0.9rem",
                  },
                },
              }}
              progressComponent={
                <div className="my-3 text-center">
                  <Triangle visible height="80" width="80" color="#5B71B9" />
                  <h5 className="mt-1">Loading...</h5>
                </div>
              }
              noDataComponent={
                <div className="text-center my-5 w-100">
                  <i className="ri-emotion-unhappy-line display-4 text-secondary" />
                  <h5 className="fw-semibold text-secondary">No Data Found</h5>
                  <p className="text-muted mb-0">
                    No transaction data available for the selected filters.
                  </p>
                </div>
              }
            />
          </div>
        </div>
      </div>
    </Container>
  );
};

export default TransactionReport;
